import { encode } from 'next-auth/jwt';
import type { AuthParams } from './authParams';
import { getApiOtt } from './getApiOtt';

// Plain (non-'use server') auth primitives shared by the Next.js middleware
// (Edge) and server code. Kept free of 'use server' so it can be imported and
// called as ordinary functions from proxy_definition.

export async function createSessionToken(sessionData: any) {
  if (!sessionData) return null;

  const secret = process.env.NEXTAUTH_SECRET;

  if (!secret) {
    throw new Error('NEXTAUTH_SECRET environment variable is not set');
  }

  const now = Math.floor(Date.now() / 1000);

  return await encode({
    token: {
      sub: sessionData.id,
      backendSessionId: sessionData.backendSessionId,
      iat: now,
      exp: now + 7200,
      jti: crypto.randomUUID(),
    },
    secret,
    maxAge: 7200,
  });
}

export async function authenticateWithBackend(params: AuthParams | null) {
  if (!params) {
    return { success: false, error: 'missing-auth-param' };
  }

  try {
    const apiCookieResponse = await getApiOtt(params);

    if (apiCookieResponse.status !== 200) {
      console.error(
        `[NextAuth] Backend auth failed with status: ${apiCookieResponse.status}`,
      );

      return { success: false, error: 'auth-failed' };
    }

    const setCookieHeader = apiCookieResponse.cookieData;

    if (!setCookieHeader || !setCookieHeader.includes('sessionid=')) {
      console.error(
        "[NextAuth] Backend response did not include a 'sessionid' cookie.",
      );

      return { success: false, error: 'no-session' };
    }

    // Extract sessionid value for JWT
    const sessionIdMatch = setCookieHeader.match(/sessionid=([^;]+)/);

    const sessionId = sessionIdMatch ? sessionIdMatch[1] : null;

    if (!sessionId) {
      console.error(
        "[NextAuth] Could not parse 'sessionid' from Set-Cookie header.",
      );

      return { success: false, error: 'invalid-session' };
    }

    const sessionData = {
      id: sessionId,
      backendSessionId: sessionId,
    };

    const token = await createSessionToken(sessionData);

    if (!token) {
      return { success: false, error: 'token-creation-failed' };
    }

    return {
      success: true,
      token,
      sessionData,
      allCookies: setCookieHeader,
    };
  } catch (error) {
    console.error(
      '[NextAuth] A network or other error occurred during authorization:',
      error,
    );

    return { success: false, error: 'network-error' };
  }
}

export async function createTokenFromSessionId(sessionid: string) {
  const sessionData = {
    id: sessionid,
    backendSessionId: sessionid,
  };

  const token = await createSessionToken(sessionData);

  if (!token) {
    return { success: false, error: 'token-creation-failed' };
  }

  return {
    success: true,
    token,
    sessionData,
    allCookies: undefined,
  };
}
